/**
 * Grouping dated loads into weeks.
 *
 * Separated from the chart that draws them so the arithmetic can be tested
 * without a renderer — the same split as `reports/paginate.ts`, and for the same
 * reason: a bar in the wrong week is a number, not a picture.
 */

export interface TrendWeek {
  /** Monday of the week, ISO. */
  startsOn: string;
  label: string;
  plannedLoad: number;
  actualLoad: number;
  matches: number;
  isCurrent?: boolean;
}

export function weeksFrom(
  sessions: readonly { onDate: string; plannedLoad: number | null; actualLoad: number | null }[],
  matches: readonly { onDate: string }[],
  weekStarts: readonly string[],
  labelFor: (startsOn: string) => string,
  today: string,
): TrendWeek[] {
  const index = new Map(
    weekStarts.map((startsOn) => [
      startsOn,
      { startsOn, label: labelFor(startsOn), plannedLoad: 0, actualLoad: 0, matches: 0 },
    ]),
  );
  const weekOf = (date: string) => {
    // The last start that is not after this date: the weeks are in order.
    let found: string | null = null;
    for (const s of weekStarts) if (s <= date) found = s;
    return found;
  };
  for (const s of sessions) {
    const bucket = index.get(weekOf(s.onDate) ?? '');
    if (!bucket) continue;
    bucket.plannedLoad += s.plannedLoad ?? 0;
    bucket.actualLoad += s.actualLoad ?? 0;
  }
  for (const m of matches) {
    const bucket = index.get(weekOf(m.onDate) ?? '');
    if (bucket) bucket.matches += 1;
  }
  const currentWeek = weekOf(today);
  return [...index.values()].map((w) => ({ ...w, isCurrent: w.startsOn === currentWeek }));
}
